import {
  registerPushDevice,
  unregisterPushDevice,
  WAVE_APP_ID,
  type WaveApiConfig,
} from "@vida/wave-core";

const DEVICE_ID_KEY = "wave:web-push:device-id";
const SUBSCRIBED_KEY = "wave:web-push:subscribed";

let vapidPublicKey = "";

export function setWaveWebPushVapidPublicKey(key: string): void {
  vapidPublicKey = key.trim();
}

export function isWebPushConfigured(): boolean {
  return Boolean(vapidPublicKey);
}

export function isWebPushSubscribed(): boolean {
  return localStorage.getItem(SUBSCRIBED_KEY) === "1";
}

function ensureDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, id);
  return id;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export async function subscribeWaveWebPush(cfg: WaveApiConfig): Promise<boolean> {
  if (!isWebPushConfigured()) return false;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;

  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
  });

  const json = subscription.toJSON();
  const keys = json.keys;
  if (!json.endpoint || !keys?.p256dh || !keys.auth) return false;

  await registerPushDevice(cfg, {
    appId: WAVE_APP_ID,
    deviceId: ensureDeviceId(),
    platform: "WEBPUSH",
    webpushSubscription: {
      endpoint: json.endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
    },
  });

  localStorage.setItem(SUBSCRIBED_KEY, "1");
  return true;
}

export async function unsubscribeWaveWebPush(cfg: WaveApiConfig): Promise<void> {
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.getRegistration("/sw.js");
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  }
  try {
    await unregisterPushDevice(cfg, {
      appId: WAVE_APP_ID,
      deviceId: ensureDeviceId(),
    });
  } catch {
    /* best effort */
  }
  localStorage.removeItem(SUBSCRIBED_KEY);
}

/** Prompt + subscribe — call only from a user gesture. */
export async function enableWaveWebPushFromGesture(cfg: WaveApiConfig): Promise<boolean> {
  try {
    return await subscribeWaveWebPush(cfg);
  } catch (err) {
    console.warn("[Wave web push] subscribe failed", err);
    return false;
  }
}
